import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Box} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useMemo, useState} from 'react'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import AppAddOrOptionsButton from 'src/components/AppAddOrOptionsButton'
import {ConfirmationModal} from 'src/components/ConfirmationModal/ConfirmationModal'
import AppReloadButton from 'src/components/appReloadButton/AppReloadButton'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {SelectWithSearchComponent} from 'src/components/selectWithSearchComponent/SelectWithSearchComponent'
import TopBar from 'src/components/top-bar/TopBar'
import useAlarmCenters from 'src/hooks/api/alarm-center/useAlarmCenters'
import useDebounce from 'src/hooks/useDebounce'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import UserOptionsMenu from 'src/pages/users/components/UserOptionsMenu'
import api from 'src/services/api'
import {AlarmCenter} from 'src/services/api/endpoints/AlarmCenterEndpoint'
import {Page} from 'src/types'
import {RenderIfConditionIsMet} from 'src/utilities/RenderIfConditionIsMet'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {QueryKeyEnum} from 'src/utilities/enums/QueryKeyEnum'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {useWindowSize} from 'usehooks-ts'
import {parseAlarmCentersSearch} from './alarmCentersHelpers'
import AlarmCenterZonesModal from './components/AlarmCenterZonesModal'
import AlarmCentersGridTable from './components/AlarmCentersGridTable'
import AlarmCentersModal from './components/AlarmCentersModal'

const searchFilterData = [
  {id: '1', label: 'Hardware ID'},
  {id: '2', label: 'Número da conta'},
  {id: '3', label: 'ID'},
]

function AlarmCenters() {
  const {selectedCompanies} = useCompaniesSelector()
  const {enqueueSnackbar} = useSnackbar()
  const queryClient = useQueryClient()
  const companyIds = useMemo(() => selectedCompanies.map((company) => company.id), [selectedCompanies])

  const [searchText, setSearchText] = useState('')
  const debouncedSearch = useDebounce(searchText, 500)
  const [searchFilterSelected, setSearchFilterSelected] = useState('Hardware ID')
  const [showSearchSelected, setShowSearchSelected] = useState<DropdownItem>({id: '1', label: 'Hardware ID'})

  const [page, setPage] = useState<Page>({page: 1, pageSize: 20})
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editingAlarmCenter, setEditingAlarmCenter] = useState<AlarmCenter | null>(null)
  const [zonesAlarmCenter, setZonesAlarmCenter] = useState<AlarmCenter | null>(null)
  const [deletingAlarmCenters, setDeletingAlarmCenters] = useState<AlarmCenter[]>([])

  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuEl)

  const {width} = useWindowSize()
  const hasCreatePermission = ValidateUserPermissions('alarm-centers:create')
  const showOptionsButton = selectedIds.length > 1

  const parsedSearch = useMemo(() => parseAlarmCentersSearch(debouncedSearch, searchFilterSelected), [debouncedSearch, searchFilterSelected])

  useEffect(() => {
    setPage((prev) => ({...prev, page: 1}))
  }, [debouncedSearch, searchFilterSelected, companyIds.join(',')])

  const {data, isLoading, isRefetching, refetch} = useAlarmCenters({
    page: page.page,
    pageSize: page.pageSize,
    companyIds,
    search: parsedSearch.search,
    filter: parsedSearch.filter,
    enabled: companyIds.length > 0,
  })

  const alarmCenters = data?.entities ?? []
  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil((data?.totalElements ?? 0) / page.pageSize))

  const clientIds = useMemo(() => Array.from(new Set(alarmCenters.map((item) => item.clientId))), [alarmCenters])

  const {data: clientsData} = useQuery({
    enabled: clientIds.length > 0,
    queryKey: [QueryKeyEnum.CLIENTS, 'by-ids', clientIds],
    queryFn: async () =>
      (
        await api.client.getMany({
          page: 1,
          pageSize: clientIds.length,
          filter: {ids: clientIds},
        })
      ).data.data.entities,
  })

  const clientNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const client of clientsData ?? []) {
      map.set(client.id, client.name)
    }
    return map
  }, [clientsData])

  const handleDeleteConfirm = async () => {
    if (!deletingAlarmCenters.length) return

    try {
      await Promise.all(deletingAlarmCenters.map((item) => api.alarmCenter.deleteOne(item.id)))
      await queryClient.invalidateQueries({queryKey: [QueryKeyEnum.ALARM_CENTERS]})
      enqueueSnackbar(deletingAlarmCenters.length > 1 ? 'Centrais de alarme removidas com sucesso' : 'Central de alarme removida com sucesso', {
        variant: 'success',
      })
      setSelectedIds([])
      setDeletingAlarmCenters([])
      void refetch()
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Não conseguimos remover a central de alarme no momento')
    }
  }

  const openDeleteConfirmation = (items: AlarmCenter[]) => {
    setDeletingAlarmCenters(items)
  }

  const deleteMessage =
    deletingAlarmCenters.length > 1
      ? `Deseja excluir ${deletingAlarmCenters.length} centrais de alarme? As zonas vinculadas também serão removidas.`
      : 'Deseja excluir esta central de alarme? As zonas vinculadas também serão removidas.'

  return (
    <Box style={styles.paper}>
      <TopBar>
        <Box display='flex' direction='row' justifyContent='space-between'>
          <Box display='flex' columnGap='16px' width={width >= 768 ? undefined : '99%'}>
            <AppReloadButton loading={isLoading || isRefetching} onClick={() => void refetch()} />
            <SelectWithSearchComponent
              hideAdvancedFilters
              searchBy={searchFilterData}
              setSearchSelected={(item) => {
                setShowSearchSelected(item)
                setSearchFilterSelected(item.label)
              }}
              searchSelected={showSearchSelected}
              inputValue={searchText}
              inputSearch={setSearchText}
              widthOnSelect='270px'
            />
          </Box>

          <RenderIfConditionIsMet condition={hasCreatePermission && width >= 768}>
            <AppAddOrOptionsButton
              showOptions={showOptionsButton}
              onClickAdd={() => setCreateModalOpen(true)}
              onClickOptions={(e) => setMenuEl(e.currentTarget)}
              text='Adicionar central'
            />
          </RenderIfConditionIsMet>
        </Box>
      </TopBar>

      {companyIds.length === 0 ? (
        <Box style={styles.emptyState}>Selecione ao menos uma empresa para visualizar as centrais de alarme.</Box>
      ) : (
        <AlarmCentersGridTable
          alarmCenters={alarmCenters}
          clientNameById={clientNameById}
          totalPages={totalPages}
          page={page}
          onPageChange={(nextPage) => setPage((prev) => ({...prev, page: nextPage}))}
          onPageSizeChange={(pageSize) => setPage((prev) => ({...prev, pageSize, page: 1}))}
          isLoading={isLoading}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onEdit={setEditingAlarmCenter}
          onManageZones={setZonesAlarmCenter}
          onDelete={(alarmCenter) => openDeleteConfirmation([alarmCenter])}
        />
      )}

      <AlarmCentersModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} />
      <AlarmCentersModal isOpen={editingAlarmCenter != null} alarmCenter={editingAlarmCenter} onClose={() => setEditingAlarmCenter(null)} />
      <AlarmCenterZonesModal isOpen={zonesAlarmCenter != null} alarmCenter={zonesAlarmCenter} onClose={() => setZonesAlarmCenter(null)} />

      <ConfirmationModal
        title={deletingAlarmCenters.length > 1 ? 'Excluir centrais' : 'Excluir central'}
        content={deleteMessage}
        isOpen={deletingAlarmCenters.length > 0}
        onClose={() => setDeletingAlarmCenters([])}
        buttonsContent={[
          {
            label: 'Cancelar',
            onClick: () => setDeletingAlarmCenters([]),
            variant: 'outlined',
            color: '#8E8E8E',
          },
          {
            label: 'Confirmar',
            onClick: () => void handleDeleteConfirm(),
            variant: 'contained',
            dangerAction: true,
          },
        ]}
      />

      <UserOptionsMenu
        open={menuOpen}
        anchorEl={menuEl}
        onClose={() => setMenuEl(null)}
        onDelete={() => {
          const items = alarmCenters.filter((item) => selectedIds.includes(item.id))
          openDeleteConfirmation(items)
          setMenuEl(null)
        }}
      />
    </Box>
  )
}

const styles = createStyleSheet({
  paper: {
    borderRadius: '8px',
    alignItems: 'center',
    flexDirection: 'column',
    background: 'white',
  },
  emptyState: {
    padding: '48px 24px',
    textAlign: 'center',
    color: '#667085',
    fontSize: '14px',
  },
})

export default AlarmCenters
